"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get LAB_QUARANTINE_NOT_OPEN () {
        return LAB_QUARANTINE_NOT_OPEN;
    },
    get LabQuarantineCountResponseSchema () {
        return LabQuarantineCountResponseSchema;
    },
    get LabQuarantineDetailSchema () {
        return LabQuarantineDetailSchema;
    },
    get LabQuarantineDismissReasonSchema () {
        return LabQuarantineDismissReasonSchema;
    },
    get LabQuarantineDismissRequestSchema () {
        return LabQuarantineDismissRequestSchema;
    },
    get LabQuarantineDismissResponseSchema () {
        return LabQuarantineDismissResponseSchema;
    },
    get LabQuarantineListItemSchema () {
        return LabQuarantineListItemSchema;
    },
    get LabQuarantineListQuerySchema () {
        return LabQuarantineListQuerySchema;
    },
    get LabQuarantineListResponseSchema () {
        return LabQuarantineListResponseSchema;
    },
    get LabQuarantinePatientDetailsSchema () {
        return LabQuarantinePatientDetailsSchema;
    },
    get LabQuarantineResolveRequestSchema () {
        return LabQuarantineResolveRequestSchema;
    },
    get LabQuarantineResolveResponseSchema () {
        return LabQuarantineResolveResponseSchema;
    },
    get LabQuarantineStatusSchema () {
        return LabQuarantineStatusSchema;
    },
    get OEM_REF_LINKED_TO_OTHER_PATIENT () {
        return OEM_REF_LINKED_TO_OTHER_PATIENT;
    },
    get PatientExternalRefListResponseSchema () {
        return PatientExternalRefListResponseSchema;
    },
    get PatientExternalRefSchema () {
        return PatientExternalRefSchema;
    }
});
const _zod = require("zod");
const _labfiles = require("./lab-files");
const _labresult = require("./lab-result");
const _result = require("../emra/result");
const LabQuarantineStatusSchema = _zod.z.enum([
    'open',
    'resolved',
    'dismissed'
]);
const LabQuarantineDismissReasonSchema = _zod.z.enum([
    'not_our_patient',
    'duplicate',
    'test_data',
    'other'
]);
const LabQuarantinePatientDetailsSchema = _zod.z.object({
    name: _zod.z.string().min(1).max(_result.EMRA_PATIENT_NAME_MAX_LENGTH).nullable(),
    birthDate: _zod.z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
    sex: _zod.z.enum([
        'F',
        'M'
    ]).nullable()
}).strict();
/** Fields every quarantine item response has. No patient details. */ const quarantineListItemShape = {
    id: _zod.z.string().uuid(),
    status: LabQuarantineStatusSchema,
    testName: _zod.z.string().nullable(),
    abnormalFlag: _labresult.LabResultFlagSchema.nullable(),
    resultedAt: _zod.z.string().datetime({
        offset: true
    }).nullable(),
    receivedAt: _zod.z.string().datetime({
        offset: true
    }),
    sourceOem: _zod.z.string(),
    attachmentCount: _zod.z.number().int().min(0)
};
/**
 * The body rule the table's CHECKs enforce: no body once resolved (it
 * moved to `lab_results`), and a body otherwise.
 */ function checkBodyLifecycle(item, ctx) {
    const isResolved = item.status === 'resolved';
    const bodyFields = [
        [
            'testName',
            item.testName
        ],
        [
            'abnormalFlag',
            item.abnormalFlag
        ]
    ];
    if (item.resultSummary !== undefined) {
        bodyFields.push([
            'resultSummary',
            item.resultSummary
        ]);
    }
    for (const [path, value] of bodyFields){
        if (isResolved !== (value === null)) {
            ctx.addIssue({
                code: 'custom',
                path: [
                    path
                ],
                message: `${path} must be null if and only if status is resolved.`
            });
        }
    }
}
/**
 * The full lifecycle for one opened item, as the table's CHECKs enforce
 * it: patient details only while open, plus the body rule above.
 */ function checkDetailLifecycle(item, ctx) {
    const isOpen = item.status === 'open';
    if (isOpen !== (item.patientDetails !== null)) {
        ctx.addIssue({
            code: 'custom',
            path: [
                'patientDetails'
            ],
            message: 'patientDetails must be set if and only if status is open.'
        });
    }
    checkBodyLifecycle(item, ctx);
}
const LabQuarantineListItemSchema = _zod.z.object(quarantineListItemShape).strict().superRefine(checkBodyLifecycle);
const LabQuarantineListQuerySchema = _zod.z.object({
    status: LabQuarantineStatusSchema.default('open'),
    page: _zod.z.coerce.number().int().min(1).default(1),
    pageSize: _zod.z.coerce.number().int().min(1).max(50).default(20)
}).strict();
const LabQuarantineListResponseSchema = _zod.z.object({
    items: _zod.z.array(LabQuarantineListItemSchema),
    page: _zod.z.number().int().min(1),
    pageSize: _zod.z.number().int().min(1).max(50),
    total: _zod.z.number().int().min(0),
    gatewayEnabled: _zod.z.boolean()
}).strict();
const LabQuarantineCountResponseSchema = _zod.z.object({
    open: _zod.z.number().int().min(0)
}).strict();
const LabQuarantineDetailSchema = _zod.z.object({
    ...quarantineListItemShape,
    patientDetails: LabQuarantinePatientDetailsSchema.nullable(),
    resultSummary: _zod.z.string().nullable(),
    attachments: _zod.z.array(_labfiles.LabFileSchema),
    sameRefOpenCount: _zod.z.number().int().min(0),
    hasOemPatientRef: _zod.z.boolean()
}).strict().superRefine(checkDetailLifecycle);
const LabQuarantineResolveRequestSchema = _zod.z.object({
    patientId: _zod.z.string().uuid()
}).strict();
const LabQuarantineResolveResponseSchema = _zod.z.object({
    id: _zod.z.string().uuid(),
    labResultId: _zod.z.string().uuid(),
    patientId: _zod.z.string().uuid(),
    refLinked: _zod.z.boolean(),
    sameRefOpenCount: _zod.z.number().int().min(0)
}).strict();
const LabQuarantineDismissRequestSchema = _zod.z.object({
    reason: LabQuarantineDismissReasonSchema
}).strict();
const LabQuarantineDismissResponseSchema = _zod.z.object({
    id: _zod.z.string().uuid(),
    status: _zod.z.literal('dismissed'),
    dismissedReason: LabQuarantineDismissReasonSchema
}).strict();
const PatientExternalRefSchema = _zod.z.object({
    id: _zod.z.string().uuid(),
    sourceOem: _zod.z.string(),
    linkedAt: _zod.z.string().datetime({
        offset: true
    }),
    linkedByUserId: _zod.z.string().uuid(),
    matchedResultCount: _zod.z.number().int().min(0)
}).strict();
const PatientExternalRefListResponseSchema = _zod.z.object({
    items: _zod.z.array(PatientExternalRefSchema),
    total: _zod.z.number().int().min(0)
}).strict();
const LAB_QUARANTINE_NOT_OPEN = 'LAB_QUARANTINE_NOT_OPEN';
const OEM_REF_LINKED_TO_OTHER_PATIENT = 'OEM_REF_LINKED_TO_OTHER_PATIENT';
